import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { NoirBackground, Button, GameCard, Pill } from '../components/GameUI';
import { Eye, EyeOff, User, VenetianMask, Ghost } from 'lucide-react';
import { toast } from 'sonner';
import { getRandomWordPair } from '../utils/wordPairs';
import { assignRoles } from '../utils/gameLogic';

const NameEntry = () => {
  const { totalPlayers, civilianCount, imposterCount, mrWhiteCount, players, setPlayers, setWordPair, setGameState, showRoles } = useGame();
  const [phase, setPhase] = useState('entering_name');
  const [idx, setIdx] = useState(0);
  const [name, setName] = useState('');
  const [names, setNames] = useState([]);
  const [assigned, setAssigned] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (players.length === totalPlayers) {
      const wp = getRandomWordPair(); setWordPair(wp);
      const r = players.map(p => ({ ...p, isEliminated: false, vote: null }));
      const a = assignRoles(r, civilianCount, imposterCount, mrWhiteCount, wp);
      setAssigned(a); setNames(a.map(p => p.name)); setPhase('pass_screen');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === 'entering_name' && inputRef.current) setTimeout(() => inputRef.current?.focus(), 100);
  }, [phase, idx]);

  const submit = () => {
    const n = name.trim();
    if (!n) { toast.error('Enter an alias'); return; }
    if (names.includes(n)) { toast.error('Alias taken'); return; }
    const nn = [...names, n]; setNames(nn); setName('');
    if (!assigned) {
      const wp = getRandomWordPair(); setWordPair(wp);
      const objs = Array.from({ length: totalPlayers }, (_, i) => ({ id: `player-${i}`, name: i === 0 ? n : '', isEliminated: false, vote: null }));
      const a = assignRoles(objs, civilianCount, imposterCount, mrWhiteCount, wp); a[0].name = n; setAssigned(a);
    } else { setAssigned(assigned.map((p, i) => i === idx ? { ...p, name: n } : p)); }
    setPhase('showing_role');
  };

  const next = () => {
    if (idx + 1 < totalPlayers) { setIdx(idx + 1); setPhase(players.length === totalPlayers ? 'pass_screen' : 'entering_name'); }
    else { setPlayers(assigned); setGameState('gameRound'); }
  };

  const pd = assigned ? assigned[idx] : null;
  const rc = (r) => r === 'civilian' ? '#E0DDD5' : r === 'imposter' ? '#EF4444' : '#FFFFFF';
  const rl = (r) => r === 'civilian' ? 'Civilian' : r === 'imposter' ? 'Imposter' : r === 'mrwhite' ? 'Mr. White' : '';
  const ri = (r) => r === 'civilian' ? <User size={36} strokeWidth={1.5} /> : r === 'imposter' ? <VenetianMask size={36} strokeWidth={1.5} /> : <Ghost size={36} strokeWidth={1.5} />;
  const rb = (r) => r === 'imposter' ? 'bright' : 'white';

  if (phase === 'pass_screen' && pd) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6 relative select-none">
        <NoirBackground />
        <motion.div key={`pass-${idx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="w-full max-w-md text-center space-y-10 relative z-10">
          <div>
            <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.4em] mb-3">Hand the device to</div>
            <div className="red-line w-16 mx-auto mb-5" />
            <h1 className="font-typewriter text-5xl text-[#E0DDD5]">{pd.name}</h1>
            <div className="mt-3"><Pill color="dark">Suspect {idx + 1} of {totalPlayers}</Pill></div>
          </div>
          <Button data-testid="tap-to-reveal-btn" color="red" onClick={() => setPhase('showing_role')} className="py-5">
            <span className="flex items-center justify-center gap-2"><Eye size={18} /> Open Envelope</span>
          </Button>
        </motion.div>
      </div>
    );
  }

  if (phase === 'showing_role' && pd) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6 relative select-none">
        <NoirBackground />
        <motion.div key={`role-${idx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="w-full max-w-md space-y-6 relative z-10">
          {showRoles && (
            <div className="text-center">
              <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.3em] mb-2">Your Identity</div>
              <motion.div className="font-typewriter text-5xl" style={{ color: rc(pd.role) }}
                initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring' }}>
                {rl(pd.role)}
              </motion.div>
            </div>
          )}
          <motion.div className="bg-[#141414] border rounded-lg p-8 text-center space-y-6"
            style={{ borderColor: `${rc(pd.role)}20` }}
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
            {showRoles && <div style={{ color: rc(pd.role) }} className="flex justify-center">{ri(pd.role)}</div>}
            <div className="white-line w-full" />
            {pd.word ? (
              <div>
                <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.3em] mb-2">Your Evidence</div>
                <div className="font-typewriter text-4xl text-[#EF4444]">{pd.word}</div>
              </div>
            ) : (
              <div>
                <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.3em] mb-2">Your Evidence</div>
                <div className="text-[#E0DDD5] font-bold text-base">No evidence found.<br />
                  <span className="text-[#5A5A5A] text-sm font-mono">Trust your instincts.</span></div>
              </div>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <Button data-testid="next-player-btn" color={rb(pd.role)} onClick={next}>
              <span className="flex items-center justify-center gap-2"><EyeOff size={16} />
                {idx + 1 < totalPlayers ? 'Seal & Pass' : 'Begin Interrogation'}</span>
            </Button>
          </motion.div>
          <div className="text-center font-mono text-[#2A2A2A] text-xs uppercase tracking-wider">{idx + 1} / {totalPlayers}</div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center p-6 pb-32 relative select-none">
      <NoirBackground />
      <motion.div key={`name-${idx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-6 relative z-10">
        <div>
          <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.3em] mb-2">Registration</div>
          <h1 className="font-typewriter text-5xl text-[#E0DDD5] leading-none">Alias</h1>
          <div className="mt-2"><Pill color="dark">Suspect {idx + 1} of {totalPlayers}</Pill></div>
        </div>
        <GameCard className="space-y-8">
          <div className="text-center"><div className="font-typewriter text-5xl text-[#EF4444]">Suspect {idx + 1}</div></div>
          <input ref={inputRef} data-testid="player-name-input" type="text" value={name}
            onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="Enter alias..."
            className="w-full bg-gradient-to-b from-[#111] to-[#0A0A0A] border border-[#333] rounded p-4 text-xl text-center text-[#E0DDD5] font-typewriter placeholder:text-[#333] focus:outline-none focus:border-[#DC2626]"
            autoFocus autoComplete="off" />
        </GameCard>
        {names.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {names.map((n, i) => <Pill key={i} color="dark">{n}</Pill>)}
          </div>
        )}
      </motion.div>
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#080808] via-[#0A0A0A]/95 to-transparent z-20">
        <div className="max-w-md mx-auto">
          <Button data-testid="submit-name-btn" color="red" onClick={submit}>
            <span className="flex items-center justify-center gap-2">Open Envelope <Eye size={18} /></span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NameEntry;
